
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../context/AppContext';
import { LiveOrder } from '../../types';
import { Bike, ShoppingBag, X, CheckCircle2, MapPin } from 'lucide-react';
import { playSynthesizedSound } from '../../utils/audioUtils';

export const OrderStatusNotification = () => {
    const { trackingOrderId, setView, isProRetailMode } = useApp();
    const [notification, setNotification] = useState<{
        show: boolean;
        title: string;
        message: string;
        type: 'DELIVERY' | 'PICKUP' | 'RETAIL';
    } | null>(null);

    useEffect(() => {
        if (!trackingOrderId) return;

        const unsub = onSnapshot(doc(db, 'orders', trackingOrderId), (docSnap) => {
            if (docSnap.exists()) {
                const order = docSnap.data() as LiveOrder;
                
                // GENERATE KEYS FOR LOCAL STORAGE (Prevents duplicate popups)
                const shippedKey = `notified_${order.id}_shipped`;
                const completedKey = `notified_${order.id}_completed`;

                // 1. SCENARIO DELIVERY: RIDER PARTITO (Stato: shipped)
                if (order.context === 'DELIVERY' && order.status === 'shipped') {
                    if (!localStorage.getItem(shippedKey)) {
                        playSynthesizedSound('success');
                        setNotification({
                            show: true,
                            title: isProRetailMode ? 'Ordine Spedito!' : 'Rider in Arrivo!',
                            message: isProRetailMode 
                                ? 'Il tuo pacco è stato affidato al corriere.' 
                                : 'Il tuo ordine è uscito dal locale ed è in consegna.',
                            type: isProRetailMode ? 'RETAIL' : 'DELIVERY'
                        });
                        localStorage.setItem(shippedKey, 'true');
                    }
                }

                // 2. SCENARIO ASPORTO: ORDINE PRONTO (Stato: completed)
                if (order.context === 'TAKE_AWAY' && order.status === 'completed') {
                    if (!localStorage.getItem(completedKey)) {
                        playSynthesizedSound('success');
                        setNotification({
                            show: true,
                            title: 'Ordine Pronto!',
                            message: isProRetailMode 
                                ? 'Puoi passare in sede a ritirare i tuoi prodotti.' 
                                : 'Il tuo ordine è pronto per il ritiro. Ti aspettiamo!',
                            type: 'PICKUP'
                        });
                        localStorage.setItem(completedKey, 'true');
                    }
                }
            }
        }, (error) => {
            console.error("Error in snapshot listener (order status):", error);
        });

        return () => unsub();
    }, [trackingOrderId, isProRetailMode]);

    if (!notification || !notification.show) return null;

    const handleClose = () => {
        setNotification(null);
    };

    const handleAction = () => {
        setNotification(null);
        // Se è delivery, porta al tracking view per vedere la mappa
        if (notification.type === 'DELIVERY' && !isProRetailMode) {
            setView('tracking');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-6 animate-in fade-in zoom-in duration-300 backdrop-blur-sm">
            <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl relative overflow-hidden flex flex-col items-center text-center p-8 border-4 border-white">
                
                {/* Background Glow */}
                <div className={`absolute top-0 left-0 right-0 h-32 opacity-20 ${notification.type === 'DELIVERY' ? 'bg-amber-500' : notification.type === 'PICKUP' ? 'bg-green-500' : 'bg-blue-500'}`}></div>

                <button 
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200 z-10"
                >
                    <X size={20}/>
                </button>

                {/* Icon Animation */}
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 relative z-10 shadow-xl ${notification.type === 'DELIVERY' ? 'bg-amber-100 text-amber-600' : notification.type === 'PICKUP' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                    <div className={`absolute inset-0 rounded-full animate-ping opacity-20 ${notification.type === 'DELIVERY' ? 'bg-amber-500' : notification.type === 'PICKUP' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                    {notification.type === 'DELIVERY' && <Bike size={48} />}
                    {notification.type === 'PICKUP' && <ShoppingBag size={48} />}
                    {notification.type === 'RETAIL' && <CheckCircle2 size={48} />}
                </div>

                <h3 className="text-2xl font-black text-gray-900 mb-2 leading-tight">
                    {notification.title}
                </h3>
                
                <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                    {notification.message}
                </p>

                <button 
                    onClick={handleAction}
                    className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${notification.type === 'DELIVERY' ? 'bg-amber-500 hover:bg-amber-600' : notification.type === 'PICKUP' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                    {notification.type === 'DELIVERY' && !isProRetailMode ? <><MapPin size={20}/> Segui Rider</> : 'Ho Capito'}
                </button>

            </div>
        </div>
    );
};
